import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react';
import { Object3D, Raycaster, Intersection, Group } from 'three';
import { hasEntityTapAction, transitionAdjustedRenderOrder } from '../../utils';
import { IEntityActionState, IEntityActionTypes, ITransCatTypes, useActionStateStore, useDataStore, useSceneStore } from '../../zustand';
import EntityLevelFadeGroup from '../fadeGroups/EntityLevelFadeGroup';
import { IEmitter, IEmitterAnimationState, IEmitterCategory, IEmitterReactProps } from '../r3f-components/component-data-structure';
import { EntityErrorBoundary } from '../r3f-components/components/boundaries';
import {
	useGetAdjustedMemoSpatialProps,
	useGetSubscribedSceneTransitionCategoryRef,
	useGetSubscribedSceneTransitionPhaseRef,
	useHocPointerDown,
	useOnEntityTestClick,
	useGetEntityIsHidden,
	useResetEntityTransformsOnPlacement,
} from './hocCustomHooks';
import { useInteraction } from '../TrackerGroups/WorldXRTracker/webxr-hooks';
import { useEnableObjectLayer, useGetSceneIndexBySceneId } from './hocCustomHooks';

interface IEmitterWrapped {
	id: string;
	sceneId: string;
	screenRelative?: boolean;
}

const withEmitterBehaviour = (WrappedEmitter: FunctionComponent<IEmitterReactProps & { category: IEmitterCategory }>): FunctionComponent<IEmitterWrapped> => {
	const FuncComp: FunctionComponent<IEmitterWrapped> = ({ id, sceneId, screenRelative = false }) => {
		const pointerIsDownRef = useRef(false);
		const pointerGroupRef = useRef<Group>(null)
		const emitterContentState = useDataStore(({ componentsById }) => componentsById?.[id] as IEmitter) || {};
		const sceneTransCatRef = useGetSubscribedSceneTransitionCategoryRef(sceneId);
		const sceneTransPhaseRef = useGetSubscribedSceneTransitionPhaseRef(sceneId);
		const sceneTransitionCategory = useSceneStore(({ transitionCategory }) => transitionCategory[sceneId]);
		const entityIsHidden = useGetEntityIsHidden(id) && screenRelative;
		const placementMode = useSceneStore(({ placementMode }) => placementMode);
		const entityActionDispatch = useActionStateStore(({ dispatch }) => dispatch);
		const enabled = sceneTransitionCategory === ITransCatTypes.enter && !placementMode;
		const sceneIndex = useGetSceneIndexBySceneId(sceneId);

		useOnEntityTestClick({ sceneId, entity: emitterContentState, sceneTransCatRef, sceneTransPhaseRef });

		useEnableObjectLayer(pointerGroupRef, hasEntityTapAction(emitterContentState), typeof sceneIndex === 'undefined' ? undefined : sceneIndex + 1);

		// Ensures that any transforms due to previous animation presets are cleared out
		useResetEntityTransformsOnPlacement(id);

		// Emitter animation behaviour specific logic
		const rawActionState = useActionStateStore(state => state.entityActionStateDict?.[id]);

		const animationState = useMemo(() => {
			return Object.keys(rawActionState || {}).reduce((acc, actionCategory) => {
				return { ...acc, ...rawActionState?.[actionCategory as keyof IEntityActionState] };
			}, {} as IEmitterAnimationState);
		}, [rawActionState]);

		// Get entity values
		const renderOrder = transitionAdjustedRenderOrder(sceneTransCatRef, (emitterContentState.renderOrder ?? 0) * 20);
		let { position, scale, rotation, zeroTuple3 } = useGetAdjustedMemoSpatialProps(emitterContentState, screenRelative);

		const onPointerDown = useHocPointerDown({ pointerIsDownRef, sceneTransCatRef, sceneTransPhaseRef, sceneId, entity: emitterContentState, screenRelative });

		useInteraction(pointerGroupRef, 'onSelect', e => {
			onPointerDown(e.intersections);
			pointerIsDownRef.current = false;
		})

		const onFinish = useCallback(() => {
			entityActionDispatch?.({
				type: IEntityActionTypes.PAUSE_EMITTER_ANIMATION_BY_ID,
				payload: [id],
			});
		}, [entityActionDispatch, id]);

		useEffect(() => {
			// Pause emitter if mounted and not autoplay or in autoplay but not enabled (wrong transition phase / placement mode)
			if (!emitterContentState.autoplay || (emitterContentState.autoplay && !enabled)) {
				entityActionDispatch?.({
					type: IEntityActionTypes.PAUSE_EMITTER_ANIMATION_BY_ID,
					payload: [id],
				});
				return;
			}
			// Otherwise play if autoplay and enabled
			entityActionDispatch?.({
				type: IEntityActionTypes.PLAY_EMITTER_ANIMATION_BY_ID,
				payload: [
					{
						id,
						duration: emitterContentState.emitter.duration ?? null,
					},
				],
			});
		}, [emitterContentState, enabled, entityActionDispatch, id]);

		// Use default duration if no animation state duration
		const duration = typeof animationState.duration === 'undefined' ? emitterContentState.emitter.duration : animationState.duration;

		return (
			<group name={`transformGroup-${id}`} position={position}>
				<group rotation={rotation}>
					<group name={`transformGroup-inner_${id}`}>
						<group name="ENTITY_VISIBLE_GROUP" visible={!entityIsHidden}>
							<EntityLevelFadeGroup name={`fadeGroup-${id}`} sceneId={sceneId} entityId={id} position={zeroTuple3} screenRelative={screenRelative}>
								<EntityErrorBoundary renderOrder={renderOrder} scale={scale} rotation={zeroTuple3}>
									<group name={`toggleVisibilityGroup-${id}`}>
										<group ref={pointerGroupRef} name={`sceneId_${sceneId}`} onPointerUp={() => (pointerIsDownRef.current = false)}>
											<WrappedEmitter
												id={id}
												category={emitterContentState.category}
												emitterGroup={emitterContentState.emitterGroup}
												emitter={emitterContentState.emitter}
												isPlaying={animationState.isPlaying}
												duration={duration}
												runCounter={animationState.runCounter}
												position={zeroTuple3}
												rotation={zeroTuple3}
												scale={scale}
												renderOrder={renderOrder}
												onPointerDown={(e) => onPointerDown(e.intersections)}
												onFinish={onFinish}
											/>
										</group>
									</group>
								</EntityErrorBoundary>
							</EntityLevelFadeGroup>
						</group>
					</group>
				</group>
			</group>
		);
	};
	FuncComp.displayName = 'withEmitterBehaviour'
	return FuncComp;
};

export default withEmitterBehaviour;

